import { useState } from "react";
import { IWorkingHour } from "../../models/IWorkingHour";
import { bulkUpsertWorkingHours } from "../services/availabilityService";
import { DAYS, apiErrorMessage, crossesMidnight, hourForDay, toHHmm, toTimeSpan } from "../utils/workingHoursUtils";
import TimeSelect from "./TimeSelect";

interface Props {
  restaurantId: string;
  hours: IWorkingHour[];
  onClose: () => void;
  onSaved: () => void;
}

interface Row { day: number; open: string; close: string; enabled: boolean }

export default function BulkHoursModal({ restaurantId, hours, onClose, onSaved }: Props) {
  const [rows, setRows] = useState<Row[]>(() =>
    DAYS.map((d) => {
      const h = hourForDay(hours, d.value);
      return h
        ? { day: d.value, open: toHHmm(h.openTime), close: toHHmm(h.closeTime), enabled: h.isEnabled }
        : { day: d.value, open: "09:00", close: "22:00", enabled: false };
    }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const update = (day: number, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.day === day ? { ...r, ...patch } : r)));

  const copyTo = (source: Row, targets: number[]) =>
    setRows((rs) => rs.map((r) => (targets.includes(r.day) ? { ...r, open: source.open, close: source.close, enabled: source.enabled } : r)));

  const invalid = rows.filter((r) => r.open === r.close);

  const save = async () => {
    if (invalid.length) { setError("Açılış ve kapanış saati aynı olamaz."); return; }
    setSaving(true);
    setError("");
    try {
      await bulkUpsertWorkingHours({
        restourantId: restaurantId,
        days: rows.map((r) => ({ dayOfWeek: r.day, openTime: toTimeSpan(r.open), closeTime: toTimeSpan(r.close), isEnabled: r.enabled })),
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, "Program kaydedilemedi."));
    } finally {
      setSaving(false);
    }
  };

  const monday = rows[0];

  return (
    <div className="wh-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="wh-modal wh-modal--wide">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg, #fef3c7, #fde68a)", color: "#d97706", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="bi bi-calendar-week-fill"></i>
            </span>
            Haftalık Programı Düzenle
          </div>
          <button onClick={onClose} disabled={saving} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: "1.15rem" }}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
        <div className="wh-hint" style={{ marginBottom: 14 }}>Bütün haftayı tek seferde kaydedin. Kapanış açılıştan önceyse vardiya ertesi güne taşar (ör. 18:00 – 02:00).</div>

        <div className="wh-chips" style={{ marginBottom: 12 }}>
          <button type="button" className="wh-chip" onClick={() => copyTo(monday, [2, 3, 4, 5])}>
            <i className="bi bi-files"></i>Pazartesiyi hafta içine kopyala
          </button>
          <button type="button" className="wh-chip" onClick={() => copyTo(monday, [2, 3, 4, 5, 6, 7])}>
            <i className="bi bi-files"></i>Pazartesiyi tüm haftaya kopyala
          </button>
          <button type="button" className="wh-chip" onClick={() => setRows((rs) => rs.map((r) => ({ ...r, enabled: true })))}>
            <i className="bi bi-check2-all"></i>Hepsini aç
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {rows.map((r) => {
            const day = DAYS[r.day - 1];
            const overnight = crossesMidnight(r.open, r.close);
            const bad = r.open === r.close;
            return (
              <div key={r.day} className={`wh-bulk-row${r.enabled ? "" : " wh-bulk-row--off"}`} style={bad ? { borderColor: "#fca5a5" } : undefined}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button type="button" className={`wh-switch ${r.enabled ? "wh-switch--on" : "wh-switch--off"}`}
                    onClick={() => update(r.day, { enabled: !r.enabled })} aria-label={`${day.label} açık/kapalı`} />
                  <span style={{ fontWeight: 800, fontSize: ".86rem", color: r.enabled ? "#111827" : "#9ca3af" }}>{day.label}</span>
                </div>
                <span style={{ fontSize: ".72rem", fontWeight: 700, color: r.enabled ? "#059669" : "#9ca3af", justifySelf: "start" }}>
                  {r.enabled ? "Açık" : "Kapalı"}
                </span>
                <div className="wh-bulk-times" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", opacity: r.enabled ? 1 : 0.55 }}>
                  <TimeSelect value={r.open} onChange={(v) => update(r.day, { open: v })} />
                  <span style={{ color: "#9ca3af" }}>–</span>
                  <TimeSelect value={r.close} onChange={(v) => update(r.day, { close: v })} />
                  {overnight && <span style={{ fontSize: ".68rem", fontWeight: 700, color: "#6366f1", background: "#eef2ff", padding: "2px 7px", borderRadius: 20 }}>+1 gün</span>}
                </div>
                <button type="button" className="wh-chip" title="Bu satırı tüm günlere uygula" style={{ padding: "5px 9px" }}
                  onClick={() => copyTo(r, DAYS.map((d) => d.value).filter((d) => d !== r.day))}>
                  <i className="bi bi-copy"></i>
                </button>
              </div>
            );
          })}
        </div>

        {error && <div className="wh-note wh-note--error" style={{ marginTop: 12 }}><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>}

        <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
          <button className="wh-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
          <button className="wh-btn wh-btn--primary" onClick={save} disabled={saving || invalid.length > 0}>
            {saving ? <><span className="spinner-border spinner-border-sm"></span>Kaydediliyor…</> : <><i className="bi bi-check-lg"></i>Haftayı Kaydet</>}
          </button>
        </div>
      </div>
    </div>
  );
}
